/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { EventsUtilsService } from 'src/app/shared/utils/events-utils.service';
import { EntityInfoService } from 'src/app/shared/services/metadata-api/entity-info.service';
import { CollapsedEventsResponse } from 'src/app/shared/models/events.interface';
import { ContextualRequestService } from './contextual-request.service';
import { DataApiService } from './data-api-service';
import { RoutingService } from 'src/app/service/routing.service';
import { QueryApiService } from 'src/app/shared/services/customer-api/query-api.service';
import { ConfigService } from 'src/app/service/config.service';
import { ParamsBuilder } from 'src/app/shared/utils/params-builder';

interface V2EventsResponse {
  events: Array<{
    count: number;
    event: { type: string; id: string; name: string };
  }>;
}

export interface SubEventRow {
  count: number;
  event: { type: 'sub_event'; id: string; name: string };
}

export interface SubEventsListResult {
  events: SubEventRow[];
  totalCount: number;
}

// Backend chunk limit inherited from the v2 `sub_events_itemized` callers.
// Safe to revisit once v5 fan-out is benchmarked end-to-end.
const ITEMIZED_CHUNK_SIZE = 100;

// v5 returns only event_id; names are denormalised from
// `metadata/hierarchy/event` (cached for the session).
@Injectable({ providedIn: 'root' })
export class SubEventsService {
  private listCache = new Map<string, Observable<SubEventsListResult>>();
  private totalCache = new Map<string, Observable<number>>();
  private itemizedCache = new Map<
    string,
    Observable<Map<string, SubEventRow[]>>
  >();
  private hierarchy$: Observable<Map<string, string>> | null = null;

  constructor(
    private eventsUtils: EventsUtilsService,
    private entityInfoService: EntityInfoService,
    private contextualRequest: ContextualRequestService,
    private dataApi: DataApiService,
    private routing: RoutingService,
    private queryApi: QueryApiService,
    private config: ConfigService,
  ) {}

  // Sub-events for an explicit (unsaved) query payload — usable outside the
  // query/search routes, where fetchSubEventsList's ContextualRequestService
  // dispatch would fall through to the entity branch.
  fetchSubEventsListForQuery(
    queryPayload: unknown,
    iparams: Record<string, any>,
  ): Observable<SubEventsListResult> {
    return this.dataApi
      .post<V2EventsResponse>(
        'search_query/events',
        queryPayload,
        this.buildQueryEventsParams(iparams),
        'v2',
      )
      .pipe(
        map((resp) => this.shapeListFromV2(resp)),
        catchError(() => of({ events: [], totalCount: 0 })),
      );
  }

  // v2 search_query/events params — shared by the routing-driven path
  // (fetchListQueryV2) and the explicit-payload path
  // (fetchSubEventsListForQuery) so the two cannot drift.
  private buildQueryEventsParams(iparams: any): any {
    const params = ParamsBuilder.build(
      iparams ?? {},
      ParamsBuilder.PARAMS_FOR_ALL_REQUESTS,
      this.config.appConfig.routing.apiproj,
    );
    params['event_type'] = 'sub_event';
    return params;
  }

  fetchSubEventsList(
    iparams: any,
    timeOverride?: string,
  ): Observable<SubEventsListResult> {
    const key = this.cacheKey(iparams, timeOverride);
    const cached = this.listCache.get(key);
    if (cached) return cached;
    const result$ = this.doFetchList(iparams, timeOverride).pipe(
      shareReplay(1),
    );
    this.listCache.set(key, result$);
    return result$;
  }

  fetchSubEventsTotal(iparams: any, timeOverride?: string): Observable<number> {
    const key = this.cacheKey(iparams, timeOverride);
    const cachedList = this.listCache.get(key);
    if (cachedList) return cachedList.pipe(map((r) => r.totalCount));
    const cachedTotal = this.totalCache.get(key);
    if (cachedTotal) return cachedTotal;
    const result$ = this.doFetchTotal(iparams, timeOverride).pipe(
      shareReplay(1),
    );
    this.totalCache.set(key, result$);
    return result$;
  }

  // Per-participant sub-events keyed by compound_key. Fans out chunked v5
  // POSTs and groups rows server-side-collapsed on every axis except
  // `event_id` and `participant`.
  fetchSubEventsItemized(
    iparams: any,
    participants: string[],
    timeOverride?: string,
    eventIds?: string[],
  ): Observable<Map<string, SubEventRow[]>> {
    if (!participants?.length) return of(new Map());
    const key = this.itemizedCacheKey(
      iparams,
      participants,
      timeOverride,
      eventIds,
    );
    const cached = this.itemizedCache.get(key);
    if (cached) return cached;
    const result$ = this.doFetchItemized(
      iparams,
      participants,
      timeOverride,
      eventIds,
    ).pipe(shareReplay(1));
    this.itemizedCache.set(key, result$);
    return result$;
  }

  clearCache(): void {
    this.listCache.clear();
    this.totalCache.clear();
    this.itemizedCache.clear();
  }

  private cacheKey(iparams: any, timeOverride?: string): string {
    return JSON.stringify({
      t: iparams?.type ?? null,
      i: iparams?.id ?? null,
      tg: iparams?.tag ?? null,
      p: iparams?.portfolioId ?? null,
      q: this.queryApi.query?.uid ?? iparams?.iquery?.uid ?? null,
      time: timeOverride ?? iparams?.time ?? null,
      u: iparams?.update ?? null,
    });
  }

  private isQueryContext(iparams: any): boolean {
    return (
      this.routing.isQuery() || this.routing.isSearch() || !!iparams?.iquery
    );
  }

  private doFetchList(
    iparams: any,
    timeOverride?: string,
  ): Observable<SubEventsListResult> {
    if (this.isQueryContext(iparams)) {
      return this.fetchListQueryV2(iparams, timeOverride);
    }
    const matrix$ = this.eventsUtils
      .fetchEventsTimeSeriesMatrix({
        time: timeOverride ?? iparams?.time,
        ignoreUrlEventIds: true,
        collapse_on: [
          'factuality',
          'temporality',
          'role',
          'participant',
          'time',
        ],
      })
      .pipe(
        catchError(() =>
          of<CollapsedEventsResponse>({ events: [], columns: [] }),
        ),
      );
    return forkJoin([matrix$, this.eventNames(iparams)]).pipe(
      map(([matrix, names]) => this.shapeList(matrix, names)),
    );
  }

  private doFetchTotal(
    iparams: any,
    timeOverride?: string,
  ): Observable<number> {
    if (this.isQueryContext(iparams)) {
      return this.fetchListQueryV2(iparams, timeOverride).pipe(
        map((r) => r.totalCount),
      );
    }
    return this.eventsUtils
      .fetchEventsTimeSeriesMatrix({
        time: timeOverride ?? iparams?.time,
        // Fold every axis — response is one row `[totalCount]`.
        collapse_on: [
          'event_id',
          'factuality',
          'temporality',
          'role',
          'participant',
          'time',
        ],
      })
      .pipe(
        map((resp) => {
          const idxCount = resp?.columns?.indexOf('count') ?? -1;
          const row = resp?.events?.[0];
          if (!row || idxCount < 0) return 0;
          return Number(row[idxCount]) || 0;
        }),
        catchError(() => of(0)),
      );
  }

  // v5 has no `search_query` filter. Until backend adds it to
  // EventFilterItemDto, query/search routes call the deprecated v2
  // `/v2/api/search_query/events?event_type=sub_event` via
  // ContextualRequestService (which handles query resolution).
  private fetchListQueryV2(
    iparams: any,
    timeOverride?: string,
  ): Observable<SubEventsListResult> {
    const sourceParams =
      timeOverride !== undefined ? { ...iparams, time: timeOverride } : iparams;
    const params = this.buildQueryEventsParams(sourceParams);
    return this.contextualRequest.request('events', iparams, params, 'v2').pipe(
      map((resp: V2EventsResponse | undefined) => this.shapeListFromV2(resp)),
      catchError(() => of({ events: [], totalCount: 0 })),
    );
  }

  private shapeListFromV2(
    resp: V2EventsResponse | undefined,
  ): SubEventsListResult {
    const rows = resp?.events ?? [];
    let total = 0;
    const events: SubEventRow[] = rows.map((row) => {
      const count = Number(row.count) || 0;
      total += count;
      return {
        count,
        event: {
          type: 'sub_event',
          id: String(row.event?.id ?? ''),
          name: String(row.event?.name ?? row.event?.id ?? ''),
        },
      };
    });
    return { events, totalCount: total };
  }

  private eventNames(iparams: any): Observable<Map<string, string>> {
    if (!this.hierarchy$) {
      this.hierarchy$ = this.entityInfoService
        .getHierarchyInfo('event', iparams ?? {})
        .pipe(
          map((resp) => this.flattenEventHierarchy(resp)),
          catchError(() => of(new Map<string, string>())),
          shareReplay(1),
        );
    }
    return this.hierarchy$;
  }

  private flattenEventHierarchy(resp: any): Map<string, string> {
    const names = new Map<string, string>();
    const walk = (nodes: any[]) => {
      if (!Array.isArray(nodes)) return;
      for (const node of nodes) {
        const e = node?.entity;
        if (e?.id && e?.name) names.set(e.id, e.name);
        if (Array.isArray(node?.children)) walk(node.children);
      }
    };
    walk(resp?.event_structure ?? []);
    return names;
  }

  private shapeList(
    matrix: CollapsedEventsResponse,
    names: Map<string, string>,
  ): SubEventsListResult {
    if (!matrix?.events?.length || !matrix?.columns?.length) {
      return { events: [], totalCount: 0 };
    }
    const idxId = matrix.columns.indexOf('event_id');
    const idxCount = matrix.columns.indexOf('count');
    if (idxId < 0 || idxCount < 0) return { events: [], totalCount: 0 };
    let total = 0;
    const events: SubEventRow[] = matrix.events.map((row) => {
      const id = String(row[idxId]);
      const count = Number(row[idxCount]) || 0;
      total += count;
      return {
        count,
        event: { type: 'sub_event', id, name: names.get(id) ?? id },
      };
    });
    return { events, totalCount: total };
  }

  private itemizedCacheKey(
    iparams: any,
    participants: string[],
    timeOverride?: string,
    eventIds?: string[],
  ): string {
    return JSON.stringify({
      p: [...participants].sort().join('|'),
      time: timeOverride ?? iparams?.time ?? null,
      e: eventIds?.length ? [...eventIds].sort().join(',') : null,
      u: iparams?.update ?? null,
    });
  }

  private doFetchItemized(
    iparams: any,
    participants: string[],
    timeOverride?: string,
    eventIds?: string[],
  ): Observable<Map<string, SubEventRow[]>> {
    const chunks: string[][] = [];
    for (let i = 0; i < participants.length; i += ITEMIZED_CHUNK_SIZE) {
      chunks.push(participants.slice(i, i + ITEMIZED_CHUNK_SIZE));
    }
    const chunk$ = chunks.map((chunk) =>
      this.eventsUtils
        .fetchEventsTimeSeriesMatrix({
          entity: chunk,
          time: timeOverride ?? iparams?.time,
          event_ids: eventIds,
          // Keep `event_id` + `participant`; fold everything else so each
          // row is `[event_id, participant, count]`.
          collapse_on: ['factuality', 'temporality', 'role', 'time'],
        })
        .pipe(
          catchError(() =>
            of<CollapsedEventsResponse>({ events: [], columns: [] }),
          ),
        ),
    );
    return forkJoin([forkJoin(chunk$), this.eventNames(iparams)]).pipe(
      map(([matrices, names]) => this.shapeItemized(matrices, names)),
    );
  }

  private shapeItemized(
    matrices: CollapsedEventsResponse[],
    names: Map<string, string>,
  ): Map<string, SubEventRow[]> {
    const result = new Map<string, SubEventRow[]>();
    for (const matrix of matrices) {
      if (!matrix?.events?.length || !matrix?.columns?.length) continue;
      const idxParticipant = matrix.columns.indexOf('participant');
      const idxId = matrix.columns.indexOf('event_id');
      const idxCount = matrix.columns.indexOf('count');
      if (idxParticipant < 0 || idxId < 0 || idxCount < 0) continue;
      for (const row of matrix.events) {
        const participant = String(row[idxParticipant]);
        const id = String(row[idxId]);
        const count = Number(row[idxCount]) || 0;
        let list = result.get(participant);
        if (!list) {
          list = [];
          result.set(participant, list);
        }
        list.push({
          count,
          event: { type: 'sub_event', id, name: names.get(id) ?? id },
        });
      }
    }
    return result;
  }
}
